import { AnimationCallbackEvent, Component, OnInit, OnDestroy, inject, input, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DispatchMethod, IDispatchCampaign } from '@common-interfaces/admin/crm/dispatch-campaign';
import { Popover, PopoverModule } from 'primeng/popover';
import { IClient } from '@common-interfaces/client';
import { Api } from '../../../../services/api.service';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import {
    BeefreeTemplateEditorComponent,
    IBeefreeTemplateEditorConfig
} from '../../shared/third-party/beefree/beefree-template-editor.component';
import {
    MailluneTemplateEditorComponent,
    IMailluneTemplateEditorConfig,
    IMailluneTemplateSaved
} from '../../shared/third-party/maillune/maillune-template-editor.component';
import { MailluneService } from '../../../../services/maillune.service';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { AuthService } from '../../../../services/auth.service';
import { IDispatchCampaignWithResults } from '../dispatch-list/dispatch-list.component';
import { Utils } from '../../../../services/utils.service';
import moment from 'moment';
import { gsap } from 'gsap';
import { TemplatePickerComponent } from '../../shared/third-party/beefree/template-selector/template-selector';
import { dispatchCampaignDefaultTemplate } from '../../shared/third-party/beefree/templates/dispatch-campaign-default';
import { validatePhone } from '../../../../../../../../../common/common-helpers/utility';

// NOTE: adjust these two paths to wherever the components live relative to this file
import { DispatchCampaignTargetFilterModeEditorComponent } from './target-filter-mode-editor/target-filter-mode-editor.component';
import { DispatchCampaignTargetFilterEditorComponent } from './target-filter-editor/target-filter-editor.component';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { StepperModule } from 'primeng/stepper';
import { CardModule } from 'primeng/card';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TabsModule } from 'primeng/tabs';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FloatLabelModule } from 'primeng/floatlabel';
import { DatePickerModule } from 'primeng/datepicker';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { Dialog } from 'primeng/dialog';
import { LoadingIconComponent } from '../../shared/loading-icon/loading-icon.component';
import { debugLogger } from '@common-helpers/debug-logger';

type selectedStep = 'WHO' | 'HOW' | 'WHAT' | 'WHEN';
@Component({
    selector: 'dispatc-administration',
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        TranslatePipe,
        PopoverModule,
        DynamicDialogModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        TagModule,
        StepperModule,
        CardModule,
        ToggleSwitchModule,
        TabsModule,
        AutoCompleteModule,
        FloatLabelModule,
        DatePickerModule,
        TextareaModule,
        TooltipModule,
        ButtonModule,
        DrawerModule,
        Dialog,
        LoadingIconComponent,
        BeefreeTemplateEditorComponent,
        MailluneTemplateEditorComponent,
        DispatchCampaignTargetFilterModeEditorComponent,
        DispatchCampaignTargetFilterEditorComponent,
        TranslatePipe,
        TranslateDirective
    ],
    templateUrl: './dispatc-administration.component.html',
    styles: ``
})
export class DispatcAdministrationComponent implements OnInit, OnDestroy {
    confirm = inject(ConfirmationService);
    translate = inject(TranslateService);
    messageService = inject(MessageService);
    router = inject(Router);
    api = inject(Api);
    dialogService = inject(DialogService);
    auth = inject(AuthService);
    campaignId = input<string>('');
    utils = inject(Utils);
    maillune = inject(MailluneService);

    readonly separatorKeysCodes = [ENTER, COMMA] as const;
    dispatchCampaign = signal<IDispatchCampaign | null>(null);
    beefreeConfig: IBeefreeTemplateEditorConfig = {
        templateJson: null,
        token: {
            access_token: '',
            v2: false
        },
        uid: ''
    };
    beefreeTemplateHasChanged = signal<boolean>(false);

    placeholders = viewChild<Popover>('placeholders');

    loading = signal<boolean>(false);
    emailLoading = signal<boolean>(false);
    smsLoading = signal<boolean>(false);

    targetFilterMode = signal<string>('');

    clients = signal<IClient[]>([]);

    beefreeTemplateEditor = viewChild<BeefreeTemplateEditorComponent>('beefreeTemplateEditor');

    mailluneConfig: IMailluneTemplateEditorConfig | null = null;
    mailluneTemplateEditor = viewChild<MailluneTemplateEditorComponent>('mailluneTemplateEditor');

    saveAsTemplateDialogVisible = signal<boolean>(false);
    saveAsTemplateName = signal<string>('');
    savingAsTemplate = signal<boolean>(false);
    savingEmailTemplate = signal<boolean>(false);

    dispatchCampaignResults: IDispatchCampaignWithResults['results'] = undefined;

    selectedStep = signal<selectedStep>('WHO');

    selectedStepValue = signal<number>(1);

    lastSavedTargetFilterJson: string = '';

    campaignRefreshHandle: ReturnType<typeof setInterval> | null = null;

    tempLongUrl = signal<string>('');

    tempTestDispatchEmailAddresses = signal<string[]>([]);
    tempTestDispatchSmsNumbers = signal<string[]>([]);

    targetFilterModeEditorVisible = signal<boolean>(false);

    primaryDispatchMethod = signal<DispatchMethod>('EMAIL');
    secondaryDispatchIsEmail = signal<boolean>(false);
    secondaryDispatchIsSms = signal<boolean>(false);

    selectedWhatTab = signal<string>('sms');

    emailEditorVisible = signal<boolean>(false);

    placeholdersVisible = signal<boolean>(false);

    shortUrlsVisible = signal<boolean>(false);

    async ngOnInit(): Promise<void> {
        let id = this.campaignId();
        if (!id) {
            return;
        }

        this.campaignRefreshHandle = setInterval(async () => {
            await this.campaignRefresh();
        }, 1000 * 30);

        let targetCampaign = await this.api.crm().get<IDispatchCampaign>(`/dispatches/${id}`);

        if (targetCampaign.success && targetCampaign.data) {
            let campaign = targetCampaign.data;
            if (!campaign.thirdParty) {
                campaign.thirdParty = {};
            }

            if (!campaign.thirdParty.beefree) {
                campaign.thirdParty.beefree = {};
            }

            if (!campaign.thirdParty.beefree.uid) {
                campaign.thirdParty.beefree.uid = `ORG-${this.auth.organizationId()}`;
            }

            if (!campaign.completedSteps) {
                campaign.completedSteps = {};
            }

            if (!campaign.method) {
                campaign.method = {
                    primaryMethod: 'EMAIL',
                    secondaryMethod: 'SMS'
                };
            }

            if (!campaign.timing) {
                campaign.timing = {
                    dispatchDate: moment().add(1, 'month').toDate()
                };
            }

            if (!campaign.emailSettings) {
                campaign.emailSettings = {
                    senderName: this.auth.currentOrganization()?.organizationName
                };
            }

            if (!campaign.smsSettings) {
                campaign.smsSettings = {
                    senderName: (this.auth.currentOrganization()?.organizationName ?? '').substring(0, 11),
                    content: ''
                };
            }

            if (!campaign.smsSettings.content) {
                debugLogger.info('Initializing SMS content with empty string to avoid issues with undefined value');
                campaign.smsSettings.content = '';
            }

            if (!campaign.targetFilter) {
                campaign.targetFilter = {
                    conditions: []
                };
            }

            if (!campaign.targetFilter.version) {
                campaign.targetFilter.version = 1;
            }

            if (!campaign.metadata) {
                campaign.metadata = {};
            }

            if (!campaign.metadata.associatedShortUrls) {
                campaign.metadata.associatedShortUrls = [];
            }

            if (!campaign.targetFilter.modeSettings) {
                campaign.targetFilter.modeSettings = {};
            }

            if (!campaign.targetFilter.modeSettings.ALL_CUSTOMERS) {
                campaign.targetFilter.modeSettings.ALL_CUSTOMERS = {
                    clientIds: []
                };
            }

            if (!campaign.targetFilter.modeSettings.SPECIFIC_PACKAGES) {
                campaign.targetFilter.modeSettings.SPECIFIC_PACKAGES = {
                    packageIds: [],
                    clientId: undefined
                };
            }

            // Publish the campaign to the signal BEFORE stepSelected(), which reads it and
            // bails out early on null. Without this the whole template never renders.
            debugLogger.info('campaign', campaign);
            this.dispatchCampaign.set(campaign);

            this.primaryDispatchMethod.set(campaign.method.primaryMethod);
            this.secondaryDispatchIsEmail.set(campaign.method.secondaryMethod == 'EMAIL');
            this.secondaryDispatchIsSms.set(campaign.method.secondaryMethod == 'SMS');

            // DRAFT campaigns (new or cloned) have never had their audience calculated, so force the
            // first save to trigger calculation even if the target filter itself is unchanged.
            this.lastSavedTargetFilterJson = campaign.status === 'DRAFT' ? '' : JSON.stringify(campaign.targetFilter.conditions);

            switch (campaign.status) {
                case 'DRAFT':
                    this.selectedStep.set('WHO');
                    break;
                case 'READY_FOR_DISPATCH':
                case 'UNSCHEDULED_DISPATCH':
                    if (campaign.completedSteps.WHAT) {
                        await this.stepSelected('WHEN');
                    } else if (campaign.completedSteps.HOW) {
                        await this.stepSelected('WHAT');
                    } else {
                        await this.stepSelected('HOW');
                    }
                    break;
            }

            if (campaign.status == 'DISPATCHED') {
                let results = await this.api.crm().get<{
                    email: {
                        sent: number;
                        delivered: number;
                        opened: number;
                        clicked: number;
                        bounced: number;
                    };
                    sms: {
                        sent: number;
                        delivered: number;
                        failed: number;
                    };
                }>(`/dispatches/${id}/results`);

                if (results.success && results.data) {
                    this.dispatchCampaignResults = results.data;
                }
            }

            this.clients.set(await this.auth.clientsInOrganization());
        }
    }

    ngOnDestroy(): void {
        if (this.campaignRefreshHandle !== null) {
            clearInterval(this.campaignRefreshHandle);
            this.campaignRefreshHandle = null;
        }
    }

    async selectedTabChanged(e: unknown): Promise<void> {
        debugLogger.info('e', e);
        debugLogger.info('this.selectedStepValue()', this.selectedStepValue());
        await this.stepSelected(this.stepFromStepValue(this.selectedStepValue()));
    }

    stepValueFromStep(step: selectedStep): number | undefined {
        switch (step) {
            case 'WHO':
                return 1;
            case 'HOW':
                return 2;
            case 'WHAT':
                return 3;
            case 'WHEN':
                return 4;
            default:
                return undefined;
        }
    }

    stepFromStepValue(stepValue: number): selectedStep | undefined {
        switch (stepValue) {
            case 1:
                return 'WHO';
            case 2:
                return 'HOW';
            case 3:
                return 'WHAT';
            case 4:
                return 'WHEN';
            default:
                return undefined;
        }
    }
    readonly steps: selectedStep[] = ['WHO', 'HOW', 'WHAT', 'WHEN'];

    /** A step can be opened once every step before it is done; the same rule stepSelected enforces. */
    canOpenStep(step: selectedStep): boolean {
        const done = this.dispatchCampaign()?.completedSteps;
        switch (step) {
            case 'WHO':
                return true;
            case 'HOW':
                return !!done?.WHO;
            case 'WHAT':
                return !!done?.HOW;
            case 'WHEN':
                return !!done?.WHAT;
        }
    }

    async stepSelected(step: selectedStep | undefined): Promise<void> {
        if (!step) {
            return;
        }

        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign || !dispatchCampaign.completedSteps || !dispatchCampaign.method) {
            return;
        }

        if (step === 'HOW' && !dispatchCampaign.completedSteps.WHO) {
            return;
        }
        if (step === 'WHAT' && !dispatchCampaign.completedSteps.HOW) {
            return;
        }
        if (step === 'WHEN' && !dispatchCampaign.completedSteps.WHAT) {
            return;
        }

        this.selectedStep.set(step);

        const stepValue = this.stepValueFromStep(step);
        if (stepValue !== undefined) {
            this.selectedStepValue.set(stepValue);
        }

        if (step === 'WHAT') {
            this.selectedWhatTab.set(dispatchCampaign.method.primaryMethod === 'EMAIL' ? 'email' : 'sms');
        }
    }

    //#region WHO
    validateTargetFilter(): boolean {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign?.targetFilter?.mode) {
            return false;
        }
        if (!dispatchCampaign.targetFilter.conditions?.length) {
            return false;
        }
        return true;
    }

    async selectTargetFilterMode(mode: string): Promise<void> {
        this.dispatchCampaign.update((campaign): any => {
            if (!campaign) {
                return campaign;
            }
            return {
                ...campaign,
                targetFilter: {
                    ...campaign.targetFilter,
                    mode
                }
            };
        });
        this.targetFilterMode.set(mode);
        this.targetFilterModeEditorVisible.set(true);
    }

    async backToTargetFilterModeSelect(): Promise<void> {
        this.dispatchCampaign.update((campaign) => {
            if (!campaign) {
                return campaign;
            }
            const { mode, ...targetFilterRest } = campaign.targetFilter;
            return {
                ...campaign,
                targetFilter: targetFilterRest as typeof campaign.targetFilter
            };
        });
    }

    async saveWho(): Promise<void> {
        await this.save();
    }

    async saveWhoAndCalculateAudience(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.dispatchCampaign.update((campaign) => {
            if (!campaign) {
                return campaign;
            }
            return {
                ...campaign,
                completedSteps: {
                    ...campaign.completedSteps,
                    WHO: true
                }
            };
        });

        await this.save();
        await this.stepSelected('HOW');
    }

    //#endregion WHO

    //#region HOW

    methodChanged(): void {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        const primaryMethod = this.primaryDispatchMethod();

        debugLogger.info(`Method changed`);
        debugLogger.info(`secondaryDispatchIsSms: ${this.secondaryDispatchIsSms()}`);
        debugLogger.info(`secondaryDispatchIsEmail: ${this.secondaryDispatchIsEmail()}`);
        debugLogger.info(`primaryDispatchMethod: ${primaryMethod}`);

        let secondaryMethod: DispatchMethod | undefined;

        if (primaryMethod === 'EMAIL') {
            this.secondaryDispatchIsEmail.set(false);
            secondaryMethod = this.secondaryDispatchIsSms() ? 'SMS' : undefined;
        } else if (primaryMethod === 'SMS') {
            this.secondaryDispatchIsSms.set(false);
            secondaryMethod = this.secondaryDispatchIsEmail() ? 'EMAIL' : undefined;
        } else {
            // no primary method selected — clear both secondary flags and the stored secondary method
            this.secondaryDispatchIsSms.set(false);
            this.secondaryDispatchIsEmail.set(false);
            secondaryMethod = undefined;
        }

        this.dispatchCampaign.update((campaign): IDispatchCampaign | null => {
            if (!campaign) {
                return campaign;
            }
            return {
                ...campaign,
                method: {
                    ...campaign.method,
                    primaryMethod,
                    secondaryMethod
                }
            };
        });

        debugLogger.info(`secondaryDispatchIsSms: ${this.secondaryDispatchIsSms()}`);
        debugLogger.info(`secondaryDispatchIsEmail: ${this.secondaryDispatchIsEmail()}`);
        debugLogger.info(`primaryDispatchMethod: ${primaryMethod}`);
    }

    getAvailablePrimaryDispatchMethods(): (DispatchMethod | undefined)[] {
        const methods: Record<string, boolean> = { EMAIL: true, SMS: true };
        const campaign = this.dispatchCampaign();
        const secondaryMethod = campaign?.method?.secondaryMethod;
        if (secondaryMethod) {
            delete methods[secondaryMethod];
        }
        return [...Object.keys(methods), undefined] as (DispatchMethod | undefined)[];
    }

    getAvailableSecondaryDispatchMethods(): (DispatchMethod | undefined)[] {
        const methods: Record<string, boolean> = { EMAIL: true, SMS: true };
        const campaign = this.dispatchCampaign();
        const primaryMethod = campaign?.method?.primaryMethod;
        if (primaryMethod) {
            delete methods[primaryMethod];
        }
        return [...Object.keys(methods), undefined] as (DispatchMethod | undefined)[];
    }

    validateHow(): boolean {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign || !dispatchCampaign.method) {
            return false;
        }

        const usesSms = dispatchCampaign.method.primaryMethod === 'SMS' || dispatchCampaign.method.secondaryMethod === 'SMS';
        const usesEmail = dispatchCampaign.method.primaryMethod === 'EMAIL' || dispatchCampaign.method.secondaryMethod === 'EMAIL';

        if (usesSms) {
            const senderName = dispatchCampaign.smsSettings?.senderName;
            if (!senderName || senderName.length < 3 || senderName.length > 11) {
                return false;
            }
        }

        if (usesEmail) {
            const senderName = dispatchCampaign.emailSettings?.senderName;
            if (!senderName || senderName.length < 3) {
                return false;
            }
        }

        return true;
    }

    async saveHow(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.dispatchCampaign.update((campaign) => {
            if (!campaign) {
                return campaign;
            }
            return {
                ...campaign,
                completedSteps: {
                    ...campaign.completedSteps,
                    HOW: true
                }
            };
        });

        await this.save();
        await this.stepSelected('WHAT');
    }
    // #endregion HOW

    //#region WHAT

    smsSenderNameChanged(): void {
        let dispatchCampaign = this.dispatchCampaign();
        if (dispatchCampaign?.smsSettings?.senderName) {
            dispatchCampaign.smsSettings.senderName = dispatchCampaign.smsSettings.senderName.substring(0, 11);
        }
    }

    showAvailablePlaceholders(event: any): void {
        let placeholders = this.placeholders();

        if (placeholders) {
            placeholders.toggle(event);
        }
    }

    copyEmailPlaceholder(placeHolder: string, event: unknown): void {
        this.utils.copyToClipboard(placeHolder);
        this.showAvailablePlaceholders(event);
    }

    closeAvailablePlaceholders(): void {
        this.placeholdersVisible.set(false);
    }

    availableEmailPlaceholders = [
        {
            placeHolder: '{FIRST_NAME}',
            label: 'CUSTOMER_FIRST_NAME',
            description: 'CUSTOMER_FIRST_NAME_DESC'
        },
        {
            placeHolder: '{LAST_NAME}',
            label: 'CUSTOMER_LAST_NAME',
            description: 'CUSTOMER_LAST_NAME_DESC'
        },
        {
            placeHolder: '{UNSUBSCRIBE_LINK}',
            label: 'UNSUBSCRIBE_LINK',
            description: 'UNSUBSCRIBE_LINK_DESC'
        }
    ];

    get mailluneEnabled(): boolean {
        return this.maillune.enabled;
    }

    // Normalises the WHAT-step preview src: Maillune stores a full data URL, Beefree raw base64.
    get emailThumbnailSrc(): string | null {
        const campaign = this.dispatchCampaign();
        const maillune = campaign?.thirdParty?.maillune?.thumbnailImageData;
        if (maillune) return maillune;
        const beefree = campaign?.thirdParty?.beefree?.thumbnailImageData;
        if (beefree) return `data:image/jpeg;base64, ${beefree}`;
        return null;
    }

    async openEmailEditor(): Promise<void> {
        // Full replacement: when the org uses Maillune, Beefree is never touched.
        if (this.mailluneEnabled) {
            if (await this.initMaillune()) {
                this.emailEditorVisible.set(true);
            }
            return;
        }

        this.emailEditorVisible.set(true);

        setTimeout(async () => {
            await this.initBeefree();
        }, 100);
    }

    async openTemplateSelector(): Promise<void> {
        // Maillune orgs never open the Beefree template picker — the editor opens directly,
        // seeded from the existing/default template which Maillune converts on load.
        if (this.mailluneEnabled) {
            if (await this.initMaillune()) {
                this.emailEditorVisible.set(true);
            }
            return;
        }

        this.dialogService.open(TemplatePickerComponent, {
            header: this.translate.instant('SELECT_TEMPLATE'),
            closable: true,
            width: '820px',
            breakpoints: {
                '880px': '92vw',
                '560px': '96vw'
            },
            contentStyle: {
                'min-height': '420px',
                'max-height': '72vh',
                overflow: 'auto'
            },
            modal: true,
            data: {
                templateSelected: async (selectedTemplate: any) => {
                    await this.initBeefree();
                    this.beefreeConfig.templateJson = selectedTemplate;
                    this.openEmailEditor();
                }
            }
        });
    }

    closeEmailEditor(): void {
        this.emailEditorVisible.set(false);
        this.mailluneConfig = null;
        this.saveAsTemplateDialogVisible.set(false);
    }

    async saveEmailTemplate(): Promise<void> {
        await this.beefreeTemplateEditor()?.save();
        this.savingEmailTemplate.set(true);
        try {
            const mailluneEditor = this.mailluneTemplateEditor();
            if (mailluneEditor) {
                await mailluneEditor.save();
            } else {
                await this.beefreeTemplateEditor()?.save();
            }
        } catch (e) {
            console.error('Failed to save email template:', e);
            this.messageService.add({ severity: 'error', summary: this.translate.instant('SAVE_CAMPAIGN_FAILED'), life: 3000 });
        } finally {
            this.savingEmailTemplate.set(false);
        }
    }

    async initBeefree(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        debugLogger.info(`Initializing Beefree editor for dispatch campaign ${dispatchCampaign.id}`);

        let locale = navigator.language || 'en-US';
        const userSettings = this.auth.currentUser()?.settings;
        const orgId = this.auth.organizationId();
        if (userSettings?.language && userSettings?.country) {
            locale = `${userSettings.language}-${userSettings.country.toUpperCase()}`;
        }

        let beeToken = await this.api.currentorganization().get<any>(`/crm/third-party/beefree/token`);

        this.beefreeConfig = {
            uid: `ORG-${orgId}`,
            token: beeToken.data,
            templateJson: dispatchCampaignDefaultTemplate,
            config: {
                autosave: 30,
                language: locale,
                contentDialog: {}
            }
        };

        if (dispatchCampaign.thirdParty?.beefree?.templateJson) {
            (this.beefreeConfig as IBeefreeTemplateEditorConfig).templateJson = dispatchCampaign.thirdParty.beefree.templateJson;
        }
    }

    private async initMaillune(): Promise<boolean> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return false;
        }

        const token = await this.maillune.getSessionToken();
        if (!token) {
            this.messageService.add({ severity: 'error', summary: this.translate.instant('EMAIL_EDITOR_UNAVAILABLE') });
            return false;
        }

        // Read-only: unlike triggers.component.ts's ensureTriggerDataInitialized() (which mutates
        // trigger sub-objects in place, the established pattern for that file), every other method
        // in this file updates the dispatchCampaign signal immutably (see beefreeTemplateSaved,
        // saveWho, etc.) — match that here rather than mutating dispatchCampaign.thirdParty directly.

        // Seed the editor with the existing Beefree design (or the default template) so Maillune
        // can convert and open it; a native Maillune design takes precedence once one exists.
        const beefreeSeed = dispatchCampaign.thirdParty?.beefree?.templateJson ?? dispatchCampaignDefaultTemplate;

        this.mailluneConfig = {
            token,
            userId: `ORG-${this.auth.organizationId()}`,
            templateJson: dispatchCampaign.thirdParty?.maillune?.templateJson,
            beefreeTemplate: beefreeSeed,
            locale: this.maillune.locale,
            theme: this.maillune.theme,
            accentColor: this.maillune.accentColor,
            mergeTags: this.maillune.buildMergeTags(this.availableEmailPlaceholders)
        };
        return true;
    }

    openTemplates(): void {
        this.mailluneTemplateEditor()?.openTemplates();
    }

    openSaveAsTemplateDialog(): void {
        this.saveAsTemplateName.set('');
        this.saveAsTemplateDialogVisible.set(true);
    }

    cancelSaveAsTemplate(): void {
        this.saveAsTemplateDialogVisible.set(false);
    }

    async confirmSaveAsTemplate(): Promise<void> {
        const name = this.saveAsTemplateName().trim();
        const editor = this.mailluneTemplateEditor();
        if (!name || !editor) return;
        this.savingAsTemplate.set(true);
        try {
            await editor.saveAsTemplate(name);
            this.messageService.add({ severity: 'success', summary: this.translate.instant('TEMPLATE_SAVED'), life: 1000 });
            this.saveAsTemplateDialogVisible.set(false);
        } catch (e) {
            console.error('Failed to save Maillune template:', e);
            this.messageService.add({ severity: 'error', summary: this.translate.instant('TEMPLATE_SAVE_FAILED') });
        } finally {
            this.savingAsTemplate.set(false);
        }
    }

    async mailluneTemplateSaved(data: IMailluneTemplateSaved): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.dispatchCampaign.set({
            ...dispatchCampaign,
            thirdParty: {
                ...dispatchCampaign.thirdParty,
                maillune: {
                    ...dispatchCampaign.thirdParty?.maillune,
                    templateJson: data.json,
                    templateHtml: data.html,
                    ...(data.thumbnail ? { thumbnailImageData: data.thumbnail } : {})
                }
            },
            completedSteps: {
                ...dispatchCampaign.completedSteps,
                WHAT: true
            }
        });

        if (this.emailEditorVisible()) {
            this.emailEditorVisible.set(false);
            this.mailluneConfig = null;
            this.saveAsTemplateDialogVisible.set(false);
        }

        await this.save();
    }

    async beefreeTemplateSaved(data: { json: string; html: string }): Promise<void> {
        debugLogger.info('Beefree template saved:', data.json);

        this.beefreeTemplateHasChanged.set(true);

        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.dispatchCampaign.set({
            ...dispatchCampaign,
            thirdParty: {
                ...dispatchCampaign.thirdParty,
                beefree: {
                    ...dispatchCampaign.thirdParty?.beefree,
                    templateJson: data.json,
                    templateHtml: data.html
                }
            },
            completedSteps: {
                ...dispatchCampaign.completedSteps,
                WHAT: true
            }
        });

        if (this.emailEditorVisible()) {
            this.emailEditorVisible.set(false);
            this.beefreeConfig = {
                templateJson: null,
                uid: '',
                token: {
                    access_token: '',
                    v2: false
                }
            };
        }

        await this.save();
    }

    openShortUrls(): void {
        this.shortUrlsVisible.set(true);
    }

    closeShortUrls(): void {
        this.shortUrlsVisible.set(false);
    }

    validateTempLongUrl(): boolean {
        try {
            new URL(this.tempLongUrl());
            return true;
        } catch (_) {
            return false;
        }
    }

    getNumberOfSmsSegments(): number {
        const dispatchCampaign = this.dispatchCampaign();
        const message = dispatchCampaign?.smsSettings?.content;
        if (!message) {
            return 0;
        }
        let messageLength = message.length;
        if (message.includes('{UNSUBSCRIBE_LINK}')) {
            messageLength += 15;
        }
        return Math.ceil(messageLength / 160);
    }

    async shortenUrl(): Promise<void> {
        if (!this.validateTempLongUrl()) {
            return;
        }

        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        const response = await this.api.crm().post<any>(`/dispatches/${dispatchCampaign.id}/shorten-url`, {
            url: this.tempLongUrl()
        });

        if (response.success && response.data) {
            this.dispatchCampaign.update((campaign) => {
                if (!campaign) {
                    return campaign;
                }
                return {
                    ...campaign,
                    metadata: {
                        ...campaign.metadata,
                        associatedShortUrls: [...(campaign.metadata?.associatedShortUrls ?? []), response.data.shortUrl]
                    }
                };
            });
            this.tempLongUrl.set('');
        }
    }

    async saveWhat(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.dispatchCampaign.update((campaign) => {
            if (!campaign) {
                return campaign;
            }
            return {
                ...campaign,
                completedSteps: {
                    ...campaign.completedSteps,
                    WHAT: true
                }
            };
        });

        await this.save();
        await this.stepSelected('WHEN');
    }

    // #endregion WHAT

    //#region WHEN

    async removeTempTestDispatchEmailAddresses(email: string): Promise<void> {
        this.tempTestDispatchEmailAddresses.update((addresses) => addresses.filter((a) => a !== email));
    }

    async addTempTestDispatchEmailAddress(event: any): Promise<void> {
        debugLogger.info('Adding email:', event.value);
        if (
            String(event.value)
                .toLowerCase()
                .match(
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                )
        ) {
            this.tempTestDispatchEmailAddresses.update((addresses) => [...addresses, event.value.trim()]);
        }
        event.chipInput!.clear();
        debugLogger.info('this.tempTestDispatchEmailAddresses()', this.tempTestDispatchEmailAddresses());
    }

    async sendTestDispatchEmails(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        const recipients = this.tempTestDispatchEmailAddresses();
        if (!recipients.length) {
            return;
        }

        this.emailLoading.set(true);

        try {
            await this.api.crm().post<any>(`/dispatches/${dispatchCampaign.id}/test-email`, {
                recipients
            });
            this.tempTestDispatchEmailAddresses.set([]);
            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('TEST_EMAIL_SENT'),
                life: 2000
            });
        } catch (error) {
            debugLogger.error('Failed to send test emails:', error);
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('TEST_EMAIL_FAILED'),
                life: 3000
            });
        } finally {
            this.emailLoading.set(false);
        }
    }

    async removeTempTestDispatchSmsNumber(number: string): Promise<void> {
        this.tempTestDispatchSmsNumbers.update((numbers) => numbers.filter((n) => n !== number));
    }

    async addTempTestDispatchSmsNumber(event: any): Promise<void> {
        if (validatePhone(event.value)) {
            this.tempTestDispatchSmsNumbers.update((numbers) => [...numbers, event.value]);
        }
        event.chipInput!.clear();
    }

    async sendTestDispatchSms(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        const recipients = this.tempTestDispatchSmsNumbers();
        if (!recipients.length) {
            return;
        }

        this.smsLoading.set(true);

        try {
            await this.api.crm().post<void>(`/dispatches/${dispatchCampaign.id}/test-sms`, {
                recipients
            });
            this.tempTestDispatchSmsNumbers.set([]);
            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('TEST_SMS_SENT'),
                life: 2000
            });
        } catch (error) {
            debugLogger.error('Failed to send test SMS:', error);
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('TEST_SMS_FAILED'),
                life: 3000
            });
        } finally {
            this.smsLoading.set(false);
        }
    }

    async saveWhen(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.dispatchCampaign.update((campaign): IDispatchCampaign | null => {
            if (!campaign) {
                return campaign;
            }
            return {
                ...campaign,
                completedSteps: {
                    ...campaign.completedSteps,
                    WHEN: true
                }
            };
        });

        await this.save();
    }

    async saveWhenAndArm(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.dispatchCampaign.update((campaign): IDispatchCampaign | null => {
            if (!campaign) {
                return campaign;
            }
            return {
                ...campaign,
                completedSteps: {
                    ...campaign.completedSteps,
                    WHEN: true
                },
                isArmed: true
            };
        });

        await this.save();
    }

    async saveWhenAndDisarm(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.dispatchCampaign.update((campaign): IDispatchCampaign | null => {
            if (!campaign) {
                return campaign;
            }
            return {
                ...campaign,
                isArmed: false
            };
        });

        await this.save();
    }

    // #endregion WHEN

    get nextStep(): string {
        switch (this.selectedStepValue()) {
            case 1:
                return this.translate.instant('SETUP_SEND_PATH');
            case 2:
                return this.translate.instant('WHAT_TO_SEND');
            case 3:
                return this.translate.instant('SCHEDULE_CAMPAIGN');
            default:
                return '';
        }
    }

    async panelSavetriggered(event: Event): Promise<void> {
        this.loading.set(true);

        const dispatchCampaign = this.dispatchCampaign();

        switch (this.selectedStepValue()) {
            case 1:
                await this.saveWhoAndCalculateAudience();
                break;
            case 2:
                await this.saveHow();
                break;
            case 3:
                await this.saveWhat();
                break;
            case 4:
                if (dispatchCampaign?.status === 'PROCESSING_TARGET_FILTER') {
                    await this.saveWhen();
                } else {
                    this.confirmDispatch(event);
                }
                break;
        }

        this.loading.set(false);
    }

    goBackToPrevStep(): void {
        const dispatchCampaign = this.dispatchCampaign();
        if (dispatchCampaign?.isArmed) {
            this.router.navigate(['/organizations', this.auth.organizationId(), 'crm', 'campaigns']);
            return;
        }

        const currentStep = this.selectedStepValue();
        if (currentStep > 1) {
            // Through stepSelected so the step name and number never disagree.
            void this.stepSelected(this.stepFromStepValue(currentStep - 1));
        } else {
            this.router.navigate(['/organizations', this.auth.organizationId(), 'crm', 'campaigns']);
        }
    }

    validateStep(): boolean {
        switch (this.selectedStepValue()) {
            case 1:
                return this.validateTargetFilter();
            case 2:
                return this.validateHow();
            default:
                return true;
        }
    }

    onEmailToggle(enabled: boolean): void {
        if (enabled) {
            const primary = this.primaryDispatchMethod();
            if (!primary) {
                this.primaryDispatchMethod.set('EMAIL');
            } else if (primary === 'SMS') {
                this.secondaryDispatchIsEmail.set(true);
            }
            // if primary is already 'EMAIL', nothing to do
        } else {
            if (this.primaryDispatchMethod() === 'EMAIL') {
                if (this.secondaryDispatchIsSms()) {
                    this.primaryDispatchMethod.set('SMS');
                    this.secondaryDispatchIsSms.set(false);
                } else {
                    this.primaryDispatchMethod.set('EMAIL');
                }
            } else {
                // primary is SMS, email was only ever secondary
                this.secondaryDispatchIsEmail.set(false);
            }
        }
        this.methodChanged();
    }

    onSmsToggle(enabled: boolean): void {
        if (enabled) {
            const primary = this.primaryDispatchMethod();
            if (!primary) {
                this.primaryDispatchMethod.set('SMS');
            } else if (primary === 'EMAIL') {
                this.secondaryDispatchIsSms.set(true);
            }
            // if primary is already 'SMS', nothing to do
        } else {
            if (this.primaryDispatchMethod() === 'SMS') {
                if (this.secondaryDispatchIsEmail()) {
                    this.primaryDispatchMethod.set('EMAIL');
                    this.secondaryDispatchIsEmail.set(false);
                } else {
                    this.primaryDispatchMethod.set('SMS');
                }
            } else {
                // primary is EMAIL, SMS was only ever secondary
                this.secondaryDispatchIsSms.set(false);
            }
        }
        this.methodChanged();
    }

    async save(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            this.loading.set(false);
            return;
        }

        this.loading.set(true);

        const targetFilterJson = JSON.stringify(dispatchCampaign.targetFilter?.conditions ?? []);
        const templateChanged = this.beefreeTemplateHasChanged();

        let updatedCampaign = dispatchCampaign;
        if (this.lastSavedTargetFilterJson !== targetFilterJson) {
            updatedCampaign = {
                ...dispatchCampaign,
                targetFilter: {
                    ...dispatchCampaign.targetFilter,
                    version: (dispatchCampaign.targetFilter?.version ?? 0) + 1
                },
                status: 'PROCESSING_TARGET_FILTER'
            };
            this.dispatchCampaign.set(updatedCampaign);
            this.lastSavedTargetFilterJson = targetFilterJson;
        }

        try {
            const savedDispatchCampaign = await this.api
                .crm()
                .patch<IDispatchCampaign>(`/dispatches/${updatedCampaign.id}?templateChanged=${templateChanged}`, updatedCampaign);

            this.beefreeTemplateHasChanged.set(false);
            if (savedDispatchCampaign.data) {
                this.dispatchCampaign.set(savedDispatchCampaign.data);
            }

            this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('SAVED_CAMPAIGN'),
                life: 1000
            });
        } catch (error) {
            debugLogger.error('Failed to save dispatch campaign:', error);
            // Roll back optimistic update so local state doesn't drift from the server
            this.dispatchCampaign.set(dispatchCampaign);
            this.lastSavedTargetFilterJson = ''; // force re-check next save attempt
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('SAVE_CAMPAIGN_FAILED'),
                life: 3000
            });
        } finally {
            this.loading.set(false);
        }
    }
    async campaignRefresh(): Promise<void> {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        if (dispatchCampaign.status === 'PROCESSING_TARGET_FILTER') {
            const refreshedCampaign = await this.api.crm().get<IDispatchCampaign>(`/dispatches/${dispatchCampaign.id}`);

            if (refreshedCampaign.success && refreshedCampaign.data && dispatchCampaign.status !== refreshedCampaign.data.status) {
                const refreshed = refreshedCampaign.data;
                this.dispatchCampaign.update((campaign) => {
                    if (!campaign) {
                        return campaign;
                    }
                    return {
                        ...campaign,
                        status: refreshed.status,
                        targetFilter: {
                            ...campaign.targetFilter,
                            preliminaryAudienceSize: refreshed.targetFilter?.preliminaryAudienceSize
                        }
                    };
                });
            }
        }
    }

    cancelDispatch(event: Event): void {
        const dispatchCampaign = this.dispatchCampaign();

        this.confirm.confirm({
            target: event.target as EventTarget,
            message: `${this.translate.instant('CANCEL_CAMPAIGN_DESC')} ${dispatchCampaign?.targetFilter?.preliminaryAudienceSize ?? 0}`,
            header: this.translate.instant('CONFIRM_CANCEL_CAMPAIGN_LABEL'),
            icon: 'pi pi-info-circle',
            rejectLabel: this.translate.instant('CANCEL'),
            rejectButtonProps: {
                label: this.translate.instant('CANCEL'),
                severity: 'secondary',
                outlined: true
            },
            acceptButtonProps: {
                label: this.translate.instant('CANCEL_DISPATCH'),
                severity: 'danger'
            },
            accept: async () => {
                await this.saveWhenAndDisarm();

                this.messageService.add({
                    severity: 'info',
                    summary: this.translate.instant('CONFIRMED'),
                    detail: this.translate.instant('CAMPAIGN_NO_LONGER_ARMED')
                });
            },
            reject: () => {}
        });
    }

    confirmDispatch(event: Event): void {
        const dispatchCampaign = this.dispatchCampaign();
        if (!dispatchCampaign) {
            return;
        }

        this.confirm.confirm({
            target: event.target as EventTarget,
            message:
                this.translate.instant('CAMPAING_DISPATCH_WARNING') +
                ' ' +
                (dispatchCampaign.targetFilter?.preliminaryAudienceSize ?? 0) +
                ' ' +
                this.translate.instant('RECEIVERS') +
                '?',
            header: this.translate.instant('CONFIRM_CAMPAIGN_LABEL'),
            icon: 'ph ph-warning',
            rejectLabel: this.translate.instant('CANCEL'),
            rejectButtonProps: {
                label: this.translate.instant('CANCEL'),
                severity: 'secondary',
                outlined: true
            },
            acceptButtonProps: {
                label: this.translate.instant('CONFIRM'),
                severity: 'success'
            },
            accept: async () => {
                await this.saveWhenAndArm();

                this.messageService.add({
                    severity: 'info',
                    summary: this.translate.instant('CONFIRMED'),
                    detail: this.translate.instant('CAMPAIGN_ARMED_DESC')
                });

                this.router.navigate(['/organizations', this.auth.organizationId(), 'crm', 'campaigns']);
            },
            reject: async () => {
                await this.saveWhenAndDisarm();

                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('REJECTED'),
                    detail: this.translate.instant('SAVE_NOT_ARMED')
                });
            }
        });
    }

    emailKeyToTranslation(key: string): string {
        switch (key) {
            case 'sent':
                return 'EMAILS_SENT';
            case 'delivered':
                return 'EMAILS_DELIVERED';
            case 'opened':
                return 'EMAILS_OPENED';
            case 'clicked':
                return 'EMAILS_CLICKED';
            case 'bounced':
                return 'EMAILS_BOUNCED';
            default:
                return '';
        }
    }

    smsKeyToTranslation(key: string): string {
        switch (key) {
            case 'sent':
                return 'EMAILS_SENT';
            case 'delivered':
                return 'EMAILS_DELIVERED';
            case 'failed':
                return 'EMAILS_FAILED';
            default:
                return '';
        }
    }

    animateStepIn(event: AnimationCallbackEvent): void {
        const cards = event.target.querySelectorAll('p-card');

        if (cards.length) {
            gsap.fromTo(
                cards,
                {
                    opacity: 0,
                    y: 10
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    ease: 'power2.inOut',
                    stagger: 0.1
                }
            );
        } else {
            gsap.fromTo(
                event.target.children,
                {
                    opacity: 0,
                    y: 10
                },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.3,
                    ease: 'power1.inOut',
                    stagger: 0.08
                }
            );
        }
    }
}
